import Vue from 'vue';
import { createAgentplaneIdentity } from './agentplane-identity';

// Public, agent-scoped embed credentials: these are sent to the browser by design.
const EMBED_URL = 'https://agentplane.ejaaz.online/embed.js';
const AGENT_ID = '96b4d95e-1427-4db3-b9f3-73f4c109108d';
const EMBED_KEY = 'ap_4300005b08fb3be26f38bede746df744d5c3a9fed597fba2';
const ACCENT = '#C8102E';
const INLINE_FRAME_SRC = `https://agentplane.ejaaz.online/embed/${AGENT_ID}?key=${encodeURIComponent(EMBED_KEY)}&display=inline`;
const INLINE_HOST_ID = 'ghonaim-agentplane-inline';
const FRAME_TITLE = 'محادثة دعم Coding With Ghonaim';

let identity;
let messageBound;
let bubbleLoading;
let inlineMounted;
let lastUnreadAt = 0;

export const supportChatState = Vue.observable({
    visible: false,
    unread: 0,
    ready: false,
    inlineVisible: false,
});

function ensureIdentity(store) {
    if (identity) return identity;
    identity = createAgentplaneIdentity(window);
    store.subscribe((mutation, state) => {
        identity.sync(state.userModule.user);
        if (mutation.type === 'SET_USER') supportChatState.unread = 0;
    });
    return identity;
}

function launcherButton() {
    return document.querySelector('button[aria-label="Open chat"], button[aria-label="Close chat"]');
}

function bubbleOpen() {
    return !!document.querySelector('button[aria-label="Close chat"]');
}

function syncVisibility() {
    supportChatState.visible = bubbleOpen() || supportChatState.inlineVisible;
}

function bindMessages() {
    if (messageBound) return;
    messageBound = true;
    window.addEventListener('message', (event) => {
        if (event.origin !== 'https://agentplane.ejaaz.online' || !event.data) return;
        if (event.data.type === 'agentplane:unread' && !supportChatState.visible) {
            // Bubble + inline iframes can both emit the same unread event.
            const now = Date.now();
            if (now - lastUnreadAt < 600) return;
            lastUnreadAt = now;
            supportChatState.unread += 1;
        }
        if (event.data.type === 'agentplane:open' || event.data.type === 'agentplane:close') {
            window.requestAnimationFrame(() => {
                syncVisibility();
                if (event.data.type === 'agentplane:open') supportChatState.unread = 0;
            });
        }
    });
}

function loadEmbedScript(params) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const url = new URL(EMBED_URL);
        Object.keys(params).forEach((key) => url.searchParams.set(key, params[key]));
        script.src = url.toString();
        script.async = true;
        script.onerror = () => {
            script.remove();
            reject(new Error('Support chat could not load'));
        };
        script.onload = () => resolve(script);
        document.head.appendChild(script);
    });
}

function attachInlineFrame(host, store) {
    host.innerHTML = '';
    const iframe = document.createElement('iframe');
    iframe.src = INLINE_FRAME_SRC;
    iframe.title = FRAME_TITLE;
    iframe.style.cssText = 'width:100%;height:100%;min-height:480px;border:0;border-radius:12px;';
    host.appendChild(iframe);
    iframe.addEventListener('load', () => identity.refresh(store.state.userModule.user));
    identity.refresh(store.state.userModule.user);
}

function inlineFrame(host) {
    return host.querySelector(`iframe[title="${FRAME_TITLE}"], iframe[title="Agent chat"]`);
}

/** Floating bubble (default Agentplane mode): launcher + collapsible / mobile full-screen panel. */
export function ensureAgentplaneWidget(store) {
    ensureIdentity(store).activate(store.state.userModule.user);
    bindMessages();

    if (bubbleLoading) return bubbleLoading;
    if (launcherButton()) {
        supportChatState.ready = true;
        identity.refresh(store.state.userModule.user);
        return Promise.resolve();
    }

    bubbleLoading = loadEmbedScript({
        agent: AGENT_ID,
        key: EMBED_KEY,
        mode: 'bubble',
        accent: ACCENT,
        push: '/agentplane-sw.js',
    }).then(() => {
        const button = launcherButton();
        if (!button) throw new Error('Support chat launcher was not created');
        button.addEventListener('click', () => window.requestAnimationFrame(syncVisibility));
        supportChatState.ready = true;
        identity.refresh(store.state.userModule.user);
        syncVisibility();
    }).catch((error) => {
        bubbleLoading = null;
        throw error;
    });

    return bubbleLoading;
}

/** Open the floating bubble panel. */
export function openAgentplaneChat() {
    const openBtn = document.querySelector('button[aria-label="Open chat"]');
    if (openBtn) {
        openBtn.click();
        supportChatState.unread = 0;
        syncVisibility();
        return true;
    }
    return bubbleOpen();
}

/**
 * Inline panel for the support page (mode=inline).
 * Bubble continues to exist site-wide via ensureAgentplaneWidget.
 */
export function mountAgentplaneSupport(container, store) {
    ensureIdentity(store).activate(store.state.userModule.user);
    bindMessages();

    // Warm the floating launcher in parallel; failures there must not block inline chat.
    ensureAgentplaneWidget(store).catch(() => {});

    container.id = INLINE_HOST_ID;
    inlineMounted = container;
    supportChatState.inlineVisible = true;
    supportChatState.unread = 0;
    syncVisibility();

    if (inlineFrame(container)) {
        identity.refresh(store.state.userModule.user);
        return Promise.resolve();
    }

    // Prefer a dedicated inline embed boot once; remounts reuse a plain iframe so we
    // do not reload embed.js (which would replace window.Agentplane again).
    if (document.querySelector(`script[src*="mode=inline"][src*="${AGENT_ID}"]`)) {
        attachInlineFrame(container, store);
        return Promise.resolve();
    }

    return loadEmbedScript({
        agent: AGENT_ID,
        key: EMBED_KEY,
        mode: 'inline',
        target: INLINE_HOST_ID,
        accent: ACCENT,
    }).then(() => {
        if (!inlineFrame(container)) {
            attachInlineFrame(container, store);
            return;
        }
        const frame = inlineFrame(container);
        frame.title = FRAME_TITLE;
        frame.addEventListener('load', () => identity.refresh(store.state.userModule.user));
        identity.refresh(store.state.userModule.user);
    });
}

export function unmountAgentplaneSupport(container) {
    if (inlineMounted && container && inlineMounted !== container) return;
    if (container) container.innerHTML = '';
    inlineMounted = null;
    supportChatState.inlineVisible = false;
    syncVisibility();
}
